import { useMemo } from 'react';
import { CURRENCIES, type MarketEvent } from '@/mocks/marketCalendar';
import { minutesToLabel, toUtcMinutes } from '../calculations';
import { CurrencyFlag } from './ui';

export default function CurrencyFocus({
  events,
  currency,
  setCurrency,
  toDisplay,
  onSelect,
}: {
  events: MarketEvent[];
  currency: string;
  setCurrency: (v: string) => void;
  toDisplay: (utc: number) => number;
  onSelect: (e: MarketEvent) => void;
}) {
  const counts = useMemo(() => {
    const map = new Map<string, { count: number; next: MarketEvent | null }>();
    for (const e of events) {
      const entry = map.get(e.currency) ?? { count: 0, next: null };
      entry.count += 1;
      if (!entry.next || toUtcMinutes(e.time) < toUtcMinutes(entry.next.time)) entry.next = e;
      map.set(e.currency, entry);
    }
    return map;
  }, [events]);

  const sorted = useMemo(() => {
    return CURRENCIES.filter((c) => counts.has(c.code)).sort((a, b) => (counts.get(b.code)?.count ?? 0) - (counts.get(a.code)?.count ?? 0));
  }, [counts]);

  const top = sorted.slice(0, 6);

  return (
    <section className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
      <h2 className="font-display text-xl font-bold tracking-tight text-slate-900 md:text-2xl">
        Bản đồ sự kiện theo tiền tệ
      </h2>
      <p className="mt-1.5 text-sm text-slate-500">Nhấn vào một đồng tiền để lọc danh sách sự kiện bên dưới.</p>

      {/* chips */}
      <div className="mt-5 flex flex-wrap gap-2">
        {sorted.map((c) => {
          const active = currency === c.code;
          const count = counts.get(c.code)?.count ?? 0;
          return (
            <button
              key={c.code}
              type="button"
              onClick={() => setCurrency(active ? 'all' : c.code)}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition-all ${
                active ? 'border-violet-600 bg-violet-600 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-violet-300'
              }`}
            >
              <CurrencyFlag code={c.code} />
              {c.code}
              <span className={`font-mono text-xs ${active ? 'text-violet-100' : 'text-slate-400'}`}>· {count}</span>
            </button>
          );
        })}
      </div>

      {/* Market focus cards */}
      <h3 className="mt-8 text-sm font-bold text-slate-800">Thị trường đáng chú ý</h3>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {top.map((c) => {
          const info = counts.get(c.code);
          const next = info?.next;
          return (
            <div key={c.code} className="rounded-2xl border border-slate-200/80 bg-slate-50/50 p-4">
              <div className="flex items-center gap-2.5">
                <CurrencyFlag code={c.code} />
                <div>
                  <p className="text-sm font-bold text-slate-800">{c.code}</p>
                  <p className="text-[11px] text-slate-400">{c.label}</p>
                </div>
                <span className="ml-auto rounded-full bg-violet-50 px-2.5 py-0.5 font-mono text-xs font-bold text-violet-700">
                  {info?.count ?? 0} sự kiện
                </span>
              </div>
              {next && (
                <button
                  type="button"
                  onClick={() => onSelect(next)}
                  className="mt-3 flex w-full items-center justify-between rounded-xl bg-white px-3 py-2 text-left ring-1 ring-slate-200 transition-colors hover:ring-violet-300"
                >
                  <span className="truncate text-xs font-medium text-slate-600">{next.title}</span>
                  <span className="ml-2 shrink-0 font-mono text-[11px] text-slate-400">
                    {minutesToLabel(toDisplay(toUtcMinutes(next.time)))}
                  </span>
                </button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}